/**
 * PageHeader — direct port of the prototype's `.main-head` block
 * (cms-redesign-mockup.html ~line 294–328). Sits at the top of every list
 * page; renders the eyebrow + title (+ optional count chip) + sub text, plus
 * a right-aligned actions cluster.
 *
 *   <PageHeader
 *     eyebrow="Content Library"
 *     title="Sessions"
 *     total="247 items"
 *     sub="Guided tapping meditations…"
 *     actions={<Button variant="coral">+ New session</Button>}
 *   />
 *
 * Structural details copied 1:1 from the prototype:
 *   • bg-bg background + 1px sand bottom border so the header visually caps
 *     the page above the toolbar/table that follows.
 *   • Decorative 340px radial-gradient (coral-soft → transparent) at the
 *     top-right, opacity 0.55 — gives the page a soft brand-tinted bloom
 *     and is the reason the header has `position: relative`.
 *   • `.title-row` uses `align-items: flex-end` so the actions sit on the
 *     same baseline as the h1's descenders, not the eyebrow above.
 *   • `.eyebrow::before` — short 16×1.5 teal-green hairline before the
 *     eyebrow text, an alignment anchor that ties the page to the brand.
 *   • `.title-left .total` chip uses JetBrains Mono 14/500 text-muted with
 *     a 6px `vertical-align` drop so the count reads as a baseline-aligned
 *     supplemental number, not part of the title.
 */
import React from 'react';
import { cn } from './cn';

export function PageHeader({ eyebrow, title, total, sub, actions, className }) {
  return (
    <div
      // `.main-head` (294–299): padding 22 28 16, bg background, relative
      // for the decorative ::before. The 1px sand bottom border lives on
      // the toolbar row below, NOT here — the prototype renders title-row
      // and toolbar inside the same .main-head box and the border-bottom
      // appears after both.
      className={cn('bg-bg relative', className)}
      style={{ padding: '22px 28px 16px' }}
    >
      {/* `.main-head::before` (300–305): 340px coral-soft radial-gradient
       *  at top-right, opacity 0.55. Behind everything, can't catch clicks. */}
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          top: 0,
          right: 0,
          width: 340,
          height: '100%',
          // coral-soft hex literal — matches the Tailwind token at runtime
          // without relying on a CSS custom property that isn't declared.
          background:
            'radial-gradient(ellipse at top right, #E58A6A18, transparent 70%)',
          opacity: 0.55,
        }}
      />

      {/* `.title-row` (306–309): flex, items END (not start), space-between,
       *  gap 24, position relative so it stacks above the gradient. */}
      <div
        className="relative flex items-end justify-between"
        style={{ gap: 24 }}
      >
        <div className="min-w-0 flex-1">
          {/* `.eyebrow` (310–316). The 16×1.5 teal-green hairline is rendered
           *  as a sibling span instead of a CSS pseudo so the React tree
           *  stays inspectable. */}
          {eyebrow && (
            <div
              className="font-condensed font-bold uppercase text-teal-green-dark flex items-center"
              style={{
                fontSize: 11,
                letterSpacing: '0.12em',
                marginBottom: 6,
                gap: 8,
              }}
            >
              <span
                aria-hidden
                className="bg-teal-green"
                style={{ width: 16, height: 1.5 }}
              />
              {eyebrow}
            </div>
          )}

          {/* `.title-left h1` (317–326): Asap Condensed 30px 700 navy,
           *  letter-spacing -0.02em, line-height 1.05. */}
          <h1
            className="font-condensed font-bold text-navy"
            style={{ fontSize: 30, letterSpacing: '-0.02em', lineHeight: 1.05 }}
          >
            {title}
            {total && (
              // `.title-left h1 .total` (322–326).
              <span
                className="font-mono text-text-muted"
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  marginLeft: 10,
                  verticalAlign: 6,
                }}
              >
                {total}
              </span>
            )}
          </h1>

          {/* `.title-left .sub` (327): mt 6, text-light, 13.5px, max-width 540. */}
          {sub && (
            <p
              className="text-text-light"
              style={{
                marginTop: 6,
                fontSize: 13.5,
                lineHeight: 1.45,
                maxWidth: 540,
              }}
            >
              {sub}
            </p>
          )}
        </div>

        {/* `.title-actions` (328): gap 8. */}
        {actions && (
          <div className="flex items-center shrink-0" style={{ gap: 8 }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

export default PageHeader;
